import { cn } from '@my/ui';
import { MyIIcon } from '@my/ui/icons';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { DEFAULT_INTEL, INTEL, type SlaAlert } from '../lib/mockData';
import type { PlanMovement } from '../lib/movements';

const TABS = ['All', 'Urgent', 'Backlog', 'Comms'] as const;
type Tab = (typeof TABS)[number];

/**
 * "my i" — Movement Intelligence. A sliding right-hand overlay (same shell,
 * colours and open/close behaviour as the Manifest screen's load-suggestions
 * panel): closed by default, opened from the pulsing MyI button in the top bar,
 * closed by clicking the MyI icon in its header. Content is mock — fit drivers,
 * feasibility and the driver brief are placeholders for the wired version.
 */
export function MovementIntelligence({
  open,
  onClose,
  selected,
}: {
  open: boolean;
  onClose: () => void;
  selected: PlanMovement | null;
}) {
  const [tab, setTab] = useState<Tab>('All');
  const intel = (selected && INTEL[selected.id]) || DEFAULT_INTEL;

  return (
    <aside
      aria-hidden={!open}
      className={cn(
        'absolute inset-y-0 right-0 z-40 flex w-[26rem] max-w-[92%] flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-300 ease-out',
        open ? 'translate-x-0' : 'pointer-events-none translate-x-full',
      )}
    >
      {/* "my i" bar — the icon closes the panel (matches Manifest). */}
      <div className="flex w-full items-center gap-2.5 border-b border-slate-700 bg-slate-800 px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close movement intelligence"
          title="Close"
          className="shrink-0 rounded-full p-0.5 transition-colors hover:bg-slate-700"
        >
          <MyIIcon className="h-7 w-7" />
        </button>
        <div className="leading-tight">
          <div className="text-xs font-medium text-slate-100">my i</div>
          <div className="text-[10px] text-slate-400">Movement Intelligence</div>
        </div>
        <span className="ml-auto whitespace-nowrap text-[9px] text-slate-200">
          Dublin Depot · Wed 22 Apr · 7 jobs
        </span>
      </div>

      {/* Tabs — styled like the Manifest panel's filter chips. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-b border-slate-200 px-3 py-2">
        {TABS.map((t) => {
          const active = t === tab;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors',
                active
                  ? 'border-slate-700 bg-slate-700 text-white'
                  : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700',
              )}
            >
              {t}
            </button>
          );
        })}
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-auto p-3 text-xs">
        {selected && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
            <div className="font-semibold text-slate-800">
              {selected.jobNo} — {selected.customer}
            </div>
            <div className="text-[11px] text-slate-500">
              {selected.cargo.join(' · ')}
              {selected.distKm != null && ` · ${selected.distKm}km · ${selected.durLabel ?? ''}`}
            </div>
          </div>
        )}

        <Block title="Fit drivers">
          <div className="space-y-1.5">
            {intel.fitDrivers.map((f) => (
              <div
                key={f.name}
                className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50/50 px-2 py-1.5"
              >
                <span className="rounded bg-emerald-600 px-1 text-[9px] font-bold text-white">FIT</span>
                <div>
                  <div className="font-medium text-slate-800">{f.name}</div>
                  <div className="text-[10px] text-slate-500">
                    {f.availLabel} · {f.statLabel}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Block>

        {intel.feasibility && (
          <Block title="Feasibility">
            <p className="flex gap-1.5 text-[11px] text-red-600">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {intel.feasibility}
            </p>
          </Block>
        )}

        <Block title="Driver brief">
          <p className="whitespace-pre-wrap rounded-md bg-slate-50 p-2 text-[11px] leading-relaxed text-slate-600">
            {intel.driverBrief}
          </p>
          <div className="mt-2 flex gap-1.5">
            <RailButton>Copy brief</RailButton>
            <RailButton>WhatsApp ↗</RailButton>
            <RailButton>App ↗</RailButton>
          </div>
        </Block>

        {intel.slaAlerts.map((a) => (
          <SlaCard key={a.jobNo} a={a} />
        ))}
      </div>
    </aside>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{title}</div>
      {children}
    </div>
  );
}

function SlaCard({ a }: { a: SlaAlert }) {
  return (
    <div className="rounded-md border border-slate-200 p-2">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-800">
        <span
          className={cn(
            'inline-block h-2 w-2 rounded-full',
            a.severity === 'high' ? 'bg-red-500' : 'bg-amber-500',
          )}
        />
        {a.jobNo} {a.title}
      </div>
      <p className="mt-1 text-[11px] text-slate-500">{a.body}</p>
      <button
        type="button"
        className="mt-1.5 rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-emerald-700"
      >
        {a.action}
      </button>
    </div>
  );
}

function RailButton({ children }: { children: React.ReactNode }) {
  return (
    <button
      type="button"
      className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
    >
      {children}
    </button>
  );
}
